<template>
  <div class="page">
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>

    <div class="header">
      <div class="title-container">
        <h1 class="title">
          <span class="title-main">AI</span>
          <span class="title-sub">{{ $t('facetool.home.titleSub') }}</span>
        </h1>
      </div>
      <div class="title-underline"></div>
    </div>

    <div class="grid">
      <button 
        class="card" 
        type="button" 
        @click="handleToolClick('FacialAssessment')"
        @mousemove="handleMouseMove"
      >
        <div class="cardCover">
          <img class="coverImg" :src="omniCover" alt="OMNICounsel - FacialAssessment" />
          <div class="cover-overlay"></div>
        </div>
        <div class="cardContent">
          <div class="cardTop">
            <span class="badge">Face</span>
            <span class="cardTitle">{{ $t('facetool.home.card1Title') }}</span>
          </div>
          <div class="cardBody">{{ $t('facetool.home.card1Desc') }}</div>
          <div class="cardFooter">
            <span class="openBtn">{{ $t('facetool.home.openTool') }}</span>
          </div>
        </div>
        <div class="spotlight"></div>
      </button>

      <button 
        class="card" 
        type="button" 
        @click="handleToolClick('FacialAgingScore', { frMode: 'angle' })"
        @mousemove="handleMouseMove"
      >
        <div class="cardCover">
          <img class="coverImg" :src="AGINGCover" alt="OMNICounsel - FacialAgingScore" />
          <div class="cover-overlay"></div>
        </div>
        <div class="cardContent">
          <div class="cardTop">
            <span class="badge">Face</span>
            <span class="cardTitle">{{ $t('facetool.home.card2Title') }}</span>
          </div>
          <div class="cardBody">{{ $t('facetool.home.card2Desc') }}</div>
          <div class="cardFooter">
            <span class="openBtn">{{ $t('facetool.home.openTool') }}</span>
          </div>
        </div>
        <div class="spotlight"></div>
      </button>

      <button 
        class="card" 
        type="button" 
        @click="handleToolClick('facialAnalysis', { aiMode: 'aiMetaFace', metaFaceType: 'All' })"
        @mousemove="handleMouseMove"
      >
        <div class="cardCover">
          <img class="coverImg" :src="AICover" alt="OMNICounsel - facialAnalysis" />
          <div class="cover-overlay"></div>
        </div>
        <div class="cardContent">
          <div class="cardTop">
            <span class="badge">Face</span>
            <span class="cardTitle">{{ $t('facetool.home.card3Title') }}</span>
          </div>
          <div class="cardBody">{{ $t('facetool.home.card3Desc') }}</div>
          <div class="cardFooter">
            <span class="openBtn">{{ $t('facetool.home.openTool') }}</span>
          </div>
        </div>
        <div class="spotlight"></div>
      </button>
    </div>

    <!-- 付款彈窗 -->
    <div v-if="showPaymentModal" class="payment-modal-overlay">
      <div class="payment-modal-content">
        <h2 class="payment-title">{{ $t('facetool.payment.title') }}</h2>
        <div class="payment-body">
          <p class="payment-intro">
            {{ $t('facetool.payment.intro') }}
          </p>

          <div class="payment-plan-box">
            <h3 class="plan-name">{{ planName || $t('facetool.payment.planName') }}</h3>
            <div class="plan-price">
              <!-- 如果有折扣，顯示原價劃線與新價格 -->
              <template v-if="originalPrice && finalPrice !== originalPrice">
                <span class="price-original" style="text-decoration: line-through; font-size: 14px; color: #94a3b8; margin-right: 8px;">${{ originalPrice }}</span>
              </template>
              <span class="price-value">${{ finalPrice }}</span> {{ currency }}
            </div>
            
            <!-- 優惠碼輸入區塊 (預留) -->
            <div class="promo-code-section" style="margin-top: 15px;">
              <div style="display: flex; gap: 8px; justify-content: center;">
                <input 
                  v-model="inputPromoCode" 
                  type="text" 
                  placeholder="輸入優惠碼" 
                  class="promo-input" 
                  style="background: rgba(255,255,255,0.1); border: 1px solid rgba(168,85,247,0.3); border-radius: 6px; padding: 6px 12px; color: #fff; width: 140px; font-size: 14px;"
                />
                <button 
                  @click="applyPromoCode"
                  class="promo-btn"
                  style="background: rgba(168,85,247,0.2); border: 1px solid rgba(168,85,247,0.5); color: #d8b4fe; border-radius: 6px; padding: 6px 12px; cursor: pointer; font-size: 14px;"
                >
                  套用
                </button>
              </div>
              <div v-if="promoMessage" :class="['promo-msg', { error: isPromoError }]" style="font-size: 12px; margin-top: 6px; color: #34d399;">
                {{ promoMessage }}
              </div>
            </div>

            <span class="plan-badge" style="margin-top: 12px;">{{ $t('facetool.payment.planBadge') }}</span>
          </div>

          <ul class="payment-features">
            <li><span class="check-icon">✅</span> <strong>{{ $t('facetool.payment.feature1Label') }}</strong> {{ $t('facetool.payment.feature1Desc') }}</li>
            <li><span class="check-icon">✅</span> <strong>{{ $t('facetool.payment.feature2Label') }}</strong> {{ $t('facetool.payment.feature2Desc') }}</li>
            <li><span class="check-icon">✅</span> <strong>{{ $t('facetool.payment.feature3Label') }}</strong> {{ $t('facetool.payment.feature3Desc') }}</li>
          </ul>
        </div>
        
        <div class="payment-actions">
          <div v-if="paypalLoading" class="paypal-loading">
            <span class="spinner"></span> {{ $t('facetool.payment.paypalLoading') }}
          </div>
          
          <div v-show="finalPrice > 0">
            <!-- PayPal 按鈕容器 -->
            <div v-show="!paypalLoading" id="paypal-button-container"></div>
          </div>
          
          <!-- 免費兌換按鈕 -->
          <div v-show="finalPrice === 0" class="free-redeem-container" style="text-align: center; margin-bottom: 12px;">
            <button 
              @click="redeemFreePromo" 
              class="free-redeem-btn"
              style="background: linear-gradient(135deg, #a855f7 0%, #7e22ce 100%); color: #fff; border: none; padding: 12px 24px; border-radius: 8px; font-size: 16px; font-weight: bold; cursor: pointer; width: 100%; box-shadow: 0 4px 14px rgba(168, 85, 247, 0.4);"
            >
              免費兌換
            </button>
          </div>
          
          <div class="payment-security">{{ $t('facetool.payment.security') }}</div>
          
          <div v-if="paymentApiError" class="payment-error-msg">{{ paymentApiError }}</div>

          <!-- 開始試用按鈕 -->
          <div v-if="canActivateTrial" class="trial-activate-container" style="margin-top: 10px; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 20px;">
            <button 
              @click="activateTrial" 
              class="payment-btn-text"
              style="color: #a855f7; font-weight: 600; width: 100%; border: 1px dashed rgba(168, 85, 247, 0.5); border-radius: 8px; padding: 10px;"
            >
              {{ $t('facetool.payment.startTrial') }}
            </button>
          </div>

          <div class="payment-footer-links">
            <a href="#" class="support-link" @click.prevent="goToSupport">{{ $t('facetool.payment.supportLink') }}</a>
            <button class="payment-btn-text" @click="closePaymentModal">{{ $t('facetool.payment.laterBtn') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 試用狀態彈窗 -->
    <div v-if="showTrialModal" class="payment-modal-overlay">
      <div class="payment-modal-content trial-modal">
        <h2 class="payment-title">{{ $t('facetool.trial.title') }}</h2>
        <div class="payment-body" style="text-align: center;">
          <div class="trial-icon" style="font-size: 50px; margin-bottom: 20px;">⏳</div>
          <p class="payment-intro">
            {{ $t('facetool.trial.description', { date: formatDate(trialEndsAt) }) }}
          </p>
        </div>
        <div class="payment-actions">
          <button @click="proceedWithTrial" class="payment-btn primary">
            {{ $t('facetool.trial.continue') }}
          </button>
          <button @click="upgradeFromTrial" class="payment-btn-text" style="color: #a855f7; font-weight: 600; text-decoration: underline;">
            {{ $t('facetool.trial.upgrade') }}
          </button>
          <button @click="showTrialModal = false" class="payment-btn-text">
            {{ $t('facetool.common.close') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 付款處理中遮罩 -->
    <div v-if="paymentProcessing" class="global-loading-overlay">
      <div class="spinner"></div>
      <div class="loading-text">{{ $t('facetool.payment.processingText') || '付款處理中，請稍候...' }}</div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router'
import { onMounted, onUnmounted, ref, watch, computed } from 'vue'
import { supabase } from '@/lib/supabase'
import { loadScript } from '@paypal/paypal-js'
import i18n from '@/i18n'

import omniCover from '@/assets/images/AppShell/tool/OMNI.jpg'
import AGINGCover from '@/assets/images/AppShell/tool/AGING.jpg'
import AICover from '@/assets/images/AppShell/tool/AI.jpg'

const router = useRouter()
const particleCanvas = ref(null)

const showPaymentModal = ref(false)
const isChecking = ref(false)
const paypalLoading = ref(true)
const paymentApiError = ref('')
const paymentProcessing = ref(false)

// 動態定價相關狀態
const planId = ref('pro_lifetime')
const planName = ref('')
const originalPrice = ref(70)
const finalPrice = ref(70)
const currency = ref('USD')
const inputPromoCode = ref('')
const currentPromoCode = ref(null)
const promoMessage = ref('')
const isPromoError = ref(false)

// 試用相關狀態
const showTrialModal = ref(false)
const trialEndsAt = ref(null)
const canActivateTrial = ref(false)
const pendingToolParams = ref(null) // 暫存使用者點擊的工具資訊

// 效能優化：判斷是否有任何彈窗開啟，用於暫停背景動畫
const isAnyModalOpen = computed(() => showPaymentModal.value || showTrialModal.value || paymentProcessing.value)

let paypalButtonInstance = null

// 監聽彈窗打開時，初始化 PayPal 按鈕
watch(showPaymentModal, async (newVal) => {
  if (newVal) {
    paymentApiError.value = ''
    // 重設狀態
    inputPromoCode.value = ''
    currentPromoCode.value = null
    promoMessage.value = ''
    isPromoError.value = false
    
    // 預載入已經在 onMounted 做過了
    // 延遲 300ms 再啟動繁重的 SDK 載入與按鈕渲染，讓視窗動畫先跑完
    setTimeout(async () => {
      if (!showPaymentModal.value) return 

      try {
        paypalLoading.value = true
        const currentClientId = process.env.VUE_APP_PAYPAL_CLIENT_ID || 'test'
        
        const paypal = await loadScript({
          'client-id': currentClientId,
          currency: currency.value,
          intent: 'capture'
        })

        if (paypal) {
          const container = document.getElementById('paypal-button-container')
          if (container) container.innerHTML = ''
          
          paypalButtonInstance = paypal.Buttons({
            style: {
              layout: 'vertical',
              color: 'gold',
              shape: 'rect',
              label: 'paypal'
            },
            createOrder: async (data, actions) => {
              paymentApiError.value = ''
              try {
                const { data: { session } } = await supabase.auth.getSession()
                const { data: orderData, error: funcErr } = await supabase.functions.invoke('create-paypal-order', {
                  headers: { Authorization: `Bearer ${session.access_token}` },
                  body: { planId: planId.value, promoCode: currentPromoCode.value }
                })
                if (funcErr || orderData?.error) throw new Error(funcErr?.message || orderData?.error || '建立訂單失敗')
                return orderData.orderID.toString()
              } catch (err) {
                paymentApiError.value = `${i18n.global.t('facetool.payment.errorTitle')} ${err.message}`
                throw err
              }
            },
            onApprove: async (data, actions) => {
              paymentProcessing.value = true
              try {
                const { data: { session } } = await supabase.auth.getSession()
                const { data: captureData, error: funcErr } = await supabase.functions.invoke('capture-paypal-order', {
                  body: { orderID: data.orderID },
                  headers: { Authorization: `Bearer ${session.access_token}` }
                })
                if (funcErr || captureData?.error) throw new Error(funcErr?.message || captureData?.error || '請款失敗')
                alert(i18n.global.t('facetool.payment.success'))
                closePaymentModal()
              } catch (err) {
                paymentApiError.value = i18n.global.t('facetool.payment.errorPayment')
              } finally {
                paymentProcessing.value = false
              }
            },
            onCancel: () => console.log('User cancelled payment'),
            onError: (err) => {
               console.error('PayPal Buttons Error:', err)
               paymentApiError.value = i18n.global.t('facetool.payment.errorModule')
            }
          })
          paypalButtonInstance.render('#paypal-button-container')
        }
      } catch (err) {
        console.error('載入 PayPal SDK 失敗', err)
        paymentApiError.value = i18n.global.t('facetool.payment.errorLoading')
      } finally {
        paypalLoading.value = false
      }
    }, 300)
  } else {
    if (paypalButtonInstance && paypalButtonInstance.close) {
      try { paypalButtonInstance.close() } catch (e) {}
    }
    const container = document.getElementById('paypal-button-container')
    if (container) container.innerHTML = ''
    paypalButtonInstance = null
    paypalLoading.value = true
  }
})

// 取得方案定價
async function fetchPricingPlan() {
  try {
    const { data: plan, error } = await supabase
      .from('pricing_plans')
      .select('*')
      .eq('id', planId.value)
      .single()
      
    if (error && error.code !== 'PGRST116') {
       console.warn('無法取得最新定價，使用預設值', error)
       return
    }
    
    if (plan && plan.is_active) {
       originalPrice.value = Number(plan.price)
       finalPrice.value = Number(plan.price)
       currency.value = plan.currency || 'USD'
       if (plan.name) planName.value = plan.name
    }
  } catch (err) {
    console.error('fetchPricingPlan 錯誤:', err)
  }
}

// 格式化日期
function formatDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
}

// 開通試用
async function activateTrial() {
  paymentProcessing.value = true
  paymentApiError.value = ''
  
  try {
    const { data: { session }, error: sessionErr } = await supabase.auth.getSession()
    if (sessionErr || !session) throw new Error('無法取得登入狀態，請重新登入')
    
    const { data, error: funcErr } = await supabase.functions.invoke('activate-trial')
    
    if (funcErr) throw funcErr
    if (data?.error) throw new Error(data.error)

    alert(i18n.global.t('common.saveSuccess') || '試用已開通！')
    showPaymentModal.value = false
    
    // 開通後直接進入剛才想使用的工具
    if (pendingToolParams.value) {
      const { mode, extra } = pendingToolParams.value
      goOmni(mode, extra)
    }
  } catch (err) {
    console.error('開通試用失敗:', err)
    paymentApiError.value = `開通失敗: ${err.message}`
  } finally {
    paymentProcessing.value = false
  }
}

// 試用中繼續進入工具
function proceedWithTrial() {
  showTrialModal.value = false
  if (pendingToolParams.value) {
    const { mode, extra } = pendingToolParams.value
    goOmni(mode, extra)
  }
}

// 在試用視窗點擊升級，關閉試用視窗並打開付款視窗
function upgradeFromTrial() {
  showTrialModal.value = false
  showPaymentModal.value = true
}

// 免費兌換處理邏輯
async function redeemFreePromo() {
  if (!currentPromoCode.value || finalPrice.value !== 0) {
    paymentApiError.value = '無效的免費兌換請求'
    return
  }
  
  paymentProcessing.value = true
  paymentApiError.value = ''
  
  try {
    const { data: { session }, error: sessionErr } = await supabase.auth.getSession()
    if (sessionErr || !session) {
      throw new Error('無法取得登入狀態，請重新登入')
    }
    
    const reqHeaders = {
      Authorization: `Bearer ${session.access_token}`
    }
    
    // 呼叫 redeem-free-promo Edge Function
    const { data: redeemData, error: funcErr } = await supabase.functions.invoke('redeem-free-promo', {
      headers: reqHeaders,
      body: { 
        planId: planId.value,
        promoCode: currentPromoCode.value 
      }
    })
    
    if (funcErr) {
      console.error('[DEBUG] redeem-free-promo API 網路/呼叫層級錯誤:', funcErr)
      throw new Error('呼叫 Edge Function 失敗: ' + funcErr.message)
    }
    
    if (redeemData?.error) {
       console.error('[DEBUG] redeem-free-promo API 邏輯錯誤:', redeemData.error)
       throw new Error(redeemData.error)
    }

    // 兌換成功
    alert(i18n.global.t('facetool.payment.success') || '兌換成功！已為您開通權限。')
    closePaymentModal()
    
  } catch (err) {
    console.error('免費兌換失敗:', err)
    paymentApiError.value = `${i18n.global.t('facetool.payment.errorTitle') || '錯誤:'} ${err.message || 'Error'}`
  } finally {
    paymentProcessing.value = false
  }
}

// 套用優惠碼
async function applyPromoCode() {
  const code = inputPromoCode.value.trim().toUpperCase()
  if (!code) {
    promoMessage.value = '請輸入優惠碼'
    isPromoError.value = true
    return
  }
  
  try {
    const { data: promo, error } = await supabase
      .from('promo_codes')
      .select('*')
      .eq('code', code)
      .single()
      
    if (error || !promo) {
       promoMessage.value = '無效的優惠碼'
       isPromoError.value = true
       currentPromoCode.value = null
       finalPrice.value = originalPrice.value
       return
    }
    
    if (!promo.is_active) {
       promoMessage.value = '該優惠碼已停用'
       isPromoError.value = true
       return
    }
    
    const now = new Date()
    if (promo.valid_from && new Date(promo.valid_from) > now) {
       promoMessage.value = '優惠碼尚未生效'
       isPromoError.value = true
       return
    }
    if (promo.valid_until && new Date(promo.valid_until) < now) {
       promoMessage.value = '優惠碼已過期'
       isPromoError.value = true
       return
    }
    if (promo.max_uses !== null && promo.used_count >= promo.max_uses) {
       promoMessage.value = '優惠碼使用次數已達上限'
       isPromoError.value = true
       return
    }
    
    // 計算折扣
    let discount = 0
    let newPrice = originalPrice.value
    
    if (promo.discount_type === 'percentage') {
       discount = originalPrice.value * (Number(promo.discount_value) / 100)
       newPrice = originalPrice.value - discount
    } else if (promo.discount_type === 'fixed_amount') {
       newPrice = Number(promo.discount_value)
       discount = originalPrice.value - newPrice
    }
    
    if (newPrice < 0) {
      newPrice = 0
      discount = originalPrice.value
    }
    
    finalPrice.value = Number(newPrice.toFixed(2))
    currentPromoCode.value = code
    promoMessage.value = `套用成功！最終結帳金額為 $${newPrice.toFixed(2)}`
    isPromoError.value = false
    
    // 如果有舊的 PayPal 按鈕，建議使用者不用重新整理也能直接點擊
    // 雖然 PayPal SDK 建議重建按鈕來套用新金額，但因為我們是在 createOrder 內才向 Edge Function 要訂單，
    // 而 Edge Function 是即時讀取 currentPromoCode (由 payload 傳過去)，
    // 所以套用優惠碼後直接點擊結帳也會帶入最新金額，不需要整個 re-render PayPal 按鈕。
  } catch (err) {
    console.error('檢查優惠碼錯誤:', err)
    promoMessage.value = '檢查優惠碼時發生錯誤'
    isPromoError.value = true
  }
}

async function handleToolClick(mode, extra = {}) {
  if (isChecking.value) return
  isChecking.value = true
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) {
      // 未登入，視同無權限或直接導向登入頁
      router.push('/member')
      return
    }

    const { data: profile, error: dbErr } = await supabase
      .from('user_profiles')
      .select('plan_type, is_lifetime, subscription_ends_at, has_used_trial')
      .eq('id', user.id)
      .maybeSingle()
    
    if (dbErr) {
      console.error('取得使用者資料失敗:', dbErr)
      showPaymentModal.value = true
      return
    }

    const planData = profile || {}
    let hasAccess = false
    let isTrial = false

    if (planData.is_lifetime || planData.plan_type === 'lifetime') {
      hasAccess = true
    } else if (planData.plan_type === 'pro' || planData.plan_type === 'trial') {
      if (planData.subscription_ends_at) {
        const endsAt = new Date(planData.subscription_ends_at)
        if (endsAt > new Date()) {
          hasAccess = true
          if (planData.plan_type === 'trial') {
            isTrial = true
            trialEndsAt.value = planData.subscription_ends_at
          }
        }
      }
    }

    // 暫存參數以供後續動作使用
    pendingToolParams.value = { mode, extra }

    if (hasAccess) {
      if (isTrial) {
        // 試用者彈出提示視窗
        showTrialModal.value = true
      } else {
        goOmni(mode, extra)
      }
    } else {
      // 無權限且未使用過試用，展示開通按鈕
      canActivateTrial.value = !planData.has_used_trial
      showPaymentModal.value = true
    }
  } catch (err) {
    console.error('驗證權限期間發生錯誤:', err)
    showPaymentModal.value = true
  } finally {
    isChecking.value = false
  }
}

function goOmni (mode, extra = {}) {
  router.push({
    name: 'AppOmniCounsel',
    query: { mode, ...extra }
  })
}

function closePaymentModal() {
  showPaymentModal.value = false
}

function goToSupport() {
  // TODO: 跳轉至客服頁面或開啟聯絡信箱
  router.push('/contact')
}

// 處理滑鼠移動，動態更新 CSS 變數
function handleMouseMove(e) {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  
  card.style.setProperty('--mouse-x', `${x}px`);
  card.style.setProperty('--mouse-y', `${y}px`);
}

// 星空 Canvas 邏輯
let animationFrameId = null
onMounted(() => {
  const canvas = particleCanvas.value
  const ctx = canvas.getContext('2d')
  let particles = []
  const resize = () => {
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
  }
  class Particle {
    constructor() {
      this.x = Math.random() * canvas.width
      this.y = Math.random() * canvas.height
      this.vx = (Math.random() - 0.5) * 0.2
      this.vy = (Math.random() - 0.5) * 0.2
      this.radius = Math.random() * 1.2 + 0.5
    }
    update() {
      this.x += this.vx; this.y += this.vy
      if (this.x < 0 || this.x > canvas.width) this.vx *= -1
      if (this.y < 0 || this.y > canvas.height) this.vy *= -1
    }
    draw() {
      ctx.beginPath()
      ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
      ctx.fill()
    }
  }
  const animate = () => {
    // 效能優化：當彈窗開啟時，暫停 Canvas 渲染以釋放 CPU/GPU 資源
    if (isAnyModalOpen.value) {
      animationFrameId = requestAnimationFrame(animate)
      return
    }
    
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    particles.forEach((p, i) => {
      p.update(); p.draw()
      for (let j = i + 1; j < particles.length; j++) {
        const p2 = particles[j]
        const dx = p.x - p2.x, dy = p.y - p2.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < 120) {
          ctx.beginPath()
          ctx.strokeStyle = `rgba(168, 85, 247, ${0.2 - dist / 120})`
          ctx.lineWidth = 0.5
          ctx.moveTo(p.x, p.y); ctx.lineTo(p2.x, p2.y); ctx.stroke()
        }
      }
    })
    animationFrameId = requestAnimationFrame(animate)
  }
  window.addEventListener('resize', resize); resize()
  particles = Array.from({ length: 80 }, () => new Particle())
  animate()
  
  // 效能優化：預先抓取方案定價，避免開啟彈窗時才請求網路
  fetchPricingPlan()
})
onUnmounted(() => cancelAnimationFrame(animationFrameId))
</script>

<style scoped>
.page {
  padding: 60px 20px;
  min-height: 100vh;
  background: radial-gradient(circle at 50% 30%, #150d2e 0%, #050508 100%);
  position: relative;
  z-index: 1;
}

.particle-canvas {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  z-index: -1;
  pointer-events: none;
}

/* Header 修復 */
.header {
  margin-bottom: 60px;
  text-align: center;
}
.title-container {
  filter: drop-shadow(0 0 15px rgba(168, 85, 247, 0.4));
}
.title {
  margin: 0;
  font-size: 38px;
  font-weight: 800;
  letter-spacing: 5px;
  background: linear-gradient(to right, #ffffff 30%, #a855f7 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
}
.title-underline {
  width: 80px; height: 3px;
  background: linear-gradient(90deg, transparent, #a855f7, transparent);
  margin: 15px auto 0;
}

/* Grid */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 35px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Card 與滑鼠高亮效果 */
.card {
  position: relative; /* 為了 spotlight */
  height: 420px;
  display: flex;
  flex-direction: column;
  padding: 0;
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid rgba(168, 85, 247, 0.15);
  background: rgba(255, 255, 255, 0.02); 
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  cursor: pointer;
  transition: transform 0.4s ease, border 0.4s ease;
}

.card:hover {
  transform: translateY(-10px);
  border: 1px solid rgba(168, 85, 247, 0.5);
}

/* 滑鼠中心高亮點層 */
.spotlight {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none;
  background: radial-gradient(
    600px circle at var(--mouse-x) var(--mouse-y), 
    rgba(168, 85, 247, 0.15), 
    transparent 40%
  );
  z-index: 2;
}

.cardCover {
  height: 190px;
  position: relative;
  z-index: 1;
}
.coverImg { width: 100%; height: 100%; object-fit: cover; opacity: 0.7; }
.cover-overlay {
  position: absolute; bottom: 0; left: 0; width: 100%; height: 60%;
  background: linear-gradient(to top, #0f0a1e, transparent);
}

.cardContent {
  flex: 1; padding: 24px; display: flex; flex-direction: column; 
  text-align: left; z-index: 3;
}

.badge {
  font-size: 10px; font-weight: 800; padding: 2px 12px; border-radius: 20px;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.3);
  color: #d8b4fe; margin-bottom: 15px; width: fit-content;
}

.cardTitle { font-size: 18px; font-weight: 700; color: #ffffff; }
.cardBody { flex: 1; font-size: 14px; color: #94a3b8; margin-top: 12px; }

/* 按鈕：呼吸燈效果 */
.openBtn {
  display: block; width: 100%; text-align: center; padding: 14px 0;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(168, 85, 247, 0.2);
  font-size: 14px; font-weight: 700; color: #d8b4fe;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

/* 滑動才會亮起紫色 & 呼吸燈 */
.card:hover .openBtn {
  background: linear-gradient(135deg, #a855f7 0%, #7e22ce 100%);
  color: #ffffff;
  border-color: transparent;
  /* 呼吸燈動畫 */
  animation: breathing 2s infinite ease-in-out;
}

@keyframes breathing {
  0% {
    box-shadow: 0 0 5px rgba(168, 85, 247, 0.4), 0 0 10px rgba(168, 85, 247, 0.2);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 20px rgba(168, 85, 247, 0.8), 0 0 30px rgba(168, 85, 247, 0.4);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 5px rgba(168, 85, 247, 0.4), 0 0 10px rgba(168, 85, 247, 0.2);
    transform: scale(1);
  }
}

/* 付款彈窗 */
.payment-modal-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(10, 8, 20, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.payment-modal-content {
  background: #15111f;
  border: 1px solid rgba(168, 85, 247, 0.4);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  text-align: left;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.7), 0 0 20px rgba(168, 85, 247, 0.15);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
}

.payment-title {
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  margin-top: 0;
  margin-bottom: 24px;
  text-align: center;
  background: linear-gradient(to right, #ffffff 0%, #d8b4fe 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.payment-body {
  margin-bottom: 30px;
}

.payment-intro {
  color: #e2e8f0;
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 24px;
}

.payment-plan-box {
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-bottom: 24px;
}

.plan-name {
  color: #d8b4fe;
  font-size: 16px;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.plan-price {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
}

.price-value {
  font-size: 42px;
  color: #c084fc;
}

.plan-badge {
  display: inline-block;
  background: rgba(168, 85, 247, 0.2);
  color: #e9d5ff;
  font-size: 13px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
}

.payment-features {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-features li {
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.payment-features strong {
  color: #f8fafc;
  white-space: nowrap;
}

.check-icon {
  flex-shrink: 0;
  font-size: 16px;
  transform: translateY(1px);
}

.payment-actions {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.payment-btn {
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  text-align: center;
}

.payment-btn.primary {
  background: linear-gradient(135deg, #a855f7 0%, #7e22ce 100%);
  color: #fff;
  box-shadow: 0 4px 15px rgba(168, 85, 247, 0.4);
}

.payment-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(168, 85, 247, 0.6);
  background: linear-gradient(135deg, #b062fa 0%, #8b29de 100%);
}

.payment-security {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.payment-security::before {
  content: '🔒';
  font-size: 10px;
}

.payment-footer-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 15px;
  gap: 10px;
}

.support-link {
  color: #a855f7;
  font-size: 13px;
  text-decoration: none;
  transition: color 0.3s ease;
}

.support-link:hover {
  color: #c084fc;
  text-decoration: underline;
}

.payment-btn-text {
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
  padding: 8px;
}

.payment-btn-text:hover {
  color: #94a3b8;
}

.paypal-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #fbbf24;
  padding: 15px;
  background: rgba(251, 191, 36, 0.1);
  border-radius: 12px;
  border: 1px dashed rgba(251, 191, 36, 0.4);
}

.payment-error-msg {
  color: #ef4444;
  font-size: 13px;
  text-align: center;
  margin-top: 10px;
  background: rgba(239, 68, 68, 0.1);
  padding: 8px;
  border-radius: 6px;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

#paypal-button-container {
  min-height: 45px;
  width: 100%;
}

/* 全域處理中遮罩 */
.global-loading-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.global-loading-overlay .spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(168, 85, 247, 0.3);
  border-top-color: #a855f7;
  border-radius: 50%;
  animation: global-spin 1s linear infinite;
  margin-bottom: 20px;
}

.global-loading-overlay .loading-text {
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 1px;
}

.promo-msg {
  transition: color 0.3s;
}
.promo-msg.error {
  color: #ef4444 !important;
}

@keyframes global-spin {
  to { transform: rotate(360deg); }
}
</style>